import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Send, MapPin, Mail, Calendar, Phone } from 'lucide-react';
import { toast } from 'sonner';

// Web3Forms access key from environment variable
// Get your free access key at https://web3forms.com and add it to .env as REACT_APP_WEB3FORMS_KEY
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || 'YOUR_WEB3FORMS_ACCESS_KEY';

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: '',
    date: '',
    location: '',
    details: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          from_name: 'Alita Captures Website',
          subject: `New Project Inquiry from ${formData.name} - ${formData.projectType}`,
          name: formData.name,
          email: formData.email,
          project_type: formData.projectType,
          date: formData.date,
          location: formData.location,
          project_details: formData.details,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Inquiry sent successfully! We\'ll review your request and get back to you soon.');
        setFormData({ name: '', email: '', projectType: '', date: '', location: '', details: '' });
      } else {
        toast.error('Failed to send inquiry. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    'Personal Event',
    'Business Event',
    'Social Media Content',
    'Filming-Only',
    'Other',
  ];

  return (
    <section
      id="contact"
      className="py-24 md:py-32 bg-cream"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <p className="font-sans text-xs tracking-superwide text-terracotta mb-4">
            INQUIRY
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal mb-6">
            Let's Bring Your Project to Life
          </h2>
          <p className="font-body text-base md:text-lg text-stone max-w-3xl mx-auto leading-relaxed">
            Share the details of your event or the content you're looking to create. Alita Captures will review your request and provide availability, recommendations, and next steps ensuring the right approach for your project.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact Form - Takes more space */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <div className="border border-sand p-8 md:p-12">
              <h3 className="font-serif text-2xl text-charcoal mb-8">
                Inquire About Your Project
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block font-sans text-xs tracking-widest text-charcoal mb-2"
                    >
                      NAME *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-transparent border border-sand focus:border-terracotta outline-none font-body text-charcoal transition-colors"
                      placeholder="Your full name"
                      data-testid="contact-name"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block font-sans text-xs tracking-widest text-charcoal mb-2"
                    >
                      EMAIL *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-transparent border border-sand focus:border-terracotta outline-none font-body text-charcoal transition-colors"
                      placeholder="your@email.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="projectType"
                    className="block font-sans text-xs tracking-widest text-charcoal mb-2"
                  >
                    TYPE OF PROJECT *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-transparent border border-sand focus:border-terracotta outline-none font-body text-charcoal transition-colors appearance-none cursor-pointer"
                    data-testid="contact-project-type"
                  >
                    <option value="">Select project type</option>
                    {projectTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="date"
                      className="block font-sans text-xs tracking-widest text-charcoal mb-2"
                    >
                      DATE
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-transparent border border-sand focus:border-terracotta outline-none font-body text-charcoal transition-colors"
                      data-testid="contact-date"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="location"
                      className="block font-sans text-xs tracking-widest text-charcoal mb-2"
                    >
                      LOCATION
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-transparent border border-sand focus:border-terracotta outline-none font-body text-charcoal transition-colors"
                      placeholder="City or venue"
                      data-testid="contact-location"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="details"
                    className="block font-sans text-xs tracking-widest text-charcoal mb-2"
                  >
                    PROJECT DETAILS *
                  </label>
                  <textarea
                    id="details"
                    name="details"
                    value={formData.details}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-transparent border border-sand focus:border-terracotta outline-none font-body text-charcoal transition-colors resize-none"
                    placeholder="Tell us about your project, what you're looking for, and any specific requirements..."
                    data-testid="contact-details"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full font-sans text-xs tracking-widest bg-terracotta text-cream px-8 py-4 hover:bg-charcoal transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    'SENDING...'
                  ) : (
                    <>
                      INQUIRE ABOUT YOUR PROJECT
                      <Send size={16} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="border border-sand p-8">
              <h3 className="font-serif text-xl text-charcoal mb-6">
                Contact Information
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin size={20} className="text-terracotta mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-sans text-xs tracking-widest text-charcoal mb-1">
                      LOCATION
                    </p>
                    <p className="font-body text-stone">
                      Montreal & Toronto, Canada
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail size={20} className="text-terracotta mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-sans text-xs tracking-widest text-charcoal mb-1">
                      EMAIL
                    </p>
                    <a
                      href="mailto:labadysterley@gmail.com"
                      className="font-body text-stone hover:text-terracotta transition-colors"
                    >
                      labadysterley@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={20} className="text-terracotta mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-sans text-xs tracking-widest text-charcoal mb-1">
                      PHONE
                    </p>
                    <a
                      href="tel:+12088030470"
                      className="font-body text-stone hover:text-terracotta transition-colors"
                    >
                      +1 (208) 803-0470
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-sand p-8">
              <div className="flex items-start gap-4">
                <Calendar size={20} className="text-terracotta mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-serif text-xl text-charcoal mb-3">
                    Booking Note
                  </h3>
                  <p className="font-body text-stone leading-relaxed text-sm">
                    All projects are confirmed following inquiry and consultation. Availability is limited.
                  </p>
                  <p className="font-body text-stone leading-relaxed text-sm mt-3">
                    Each inquiry is handled with care to deliver a result aligned with your vision and goals.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
